/**
 * Ammo Can Offer – Landing Page JS
 * Handles club/tier selection, add-to-cart, scroll reveals,
 * sticky mobile bar, FAQ accordion, scarcity counter, and hero entrance.
 *
 * Vanilla JS only. No dependencies.
 */

document.addEventListener('DOMContentLoaded', () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // =============================================
  // 0. HIDE THEME HEADER/FOOTER (JS failsafe for :has() CSS)
  // =============================================

  if (document.querySelector('.aco-page')) {
    document.documentElement.classList.add('aco-page-active');
    [
      '.shopify-section-group-header-group',
      '.shopify-section-group-footer-group',
      '#shopify-section-mobile-dock',
      '.shopify-section-mobile-dock'
    ].forEach(sel => {
      document.querySelectorAll(sel).forEach(el => { el.style.display = 'none'; });
    });
  }

  // =============================================
  // 1. CALIBER TOGGLE (9mm / 5.56)
  // =============================================

  document.querySelectorAll('.aco-clubs__toggle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const clubId = btn.dataset.toggleClub;

      // Toggle button active state
      document.querySelectorAll('.aco-clubs__toggle-btn').forEach(b => b.classList.remove('aco-clubs__toggle-btn--active'));
      btn.classList.add('aco-clubs__toggle-btn--active');

      // Show/hide panels
      document.querySelectorAll('.aco-club-panel').forEach(panel => {
        panel.classList.toggle('aco-club-panel--active', panel.dataset.clubPanel === clubId);
      });

      // Deselect tiers when switching calibers
      deselectAllTiers();

      // Auto-select the popular tier in the newly visible panel
      const activePanel = document.querySelector('.aco-club-panel--active');
      if (activePanel) {
        const popularTier = activePanel.querySelector('.aco-tier-row--popular');
        if (popularTier) selectTier(popularTier);
      }
    });
  });

  // =============================================
  // 2. TIER SELECTION
  // =============================================

  function deselectAllTiers() {
    document.querySelectorAll('.aco-tier-row--selected').forEach(row => {
      row.classList.remove('aco-tier-row--selected');
      row.setAttribute('aria-checked', 'false');
    });
    document.querySelectorAll('.aco-club-panel__cta').forEach(btn => {
      btn.style.display = 'none';
      btn.disabled = true;
    });
  }

  document.querySelectorAll('.aco-tier-row').forEach(row => {
    row.addEventListener('click', () => selectTier(row));
    row.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectTier(row);
      }
    });
  });

  function selectTier(row) {
    const panel = row.closest('.aco-club-panel');
    if (!panel) return;

    // Deselect all tiers across all panels
    deselectAllTiers();

    row.classList.add('aco-tier-row--selected');
    row.setAttribute('aria-checked', 'true');

    const variantId = row.dataset.variantId || '';
    const sellingPlanId = row.dataset.sellingPlanId || '';
    const price = row.dataset.price || '';

    const cta = panel.querySelector('.aco-club-panel__cta');
    if (cta) {
      cta.style.display = '';
      cta.disabled = false;
      cta.dataset.variantId = variantId;
      cta.dataset.sellingPlanId = sellingPlanId;

      const ctaText = cta.querySelector('.aco-club-card__cta-text');
      if (ctaText) {
        ctaText.textContent = 'JOIN NOW \u2014 ' + price + '/MO';
      }
    }
  }

  // =============================================
  // 3. ADD TO CART (ammo can + subscription + insurance → checkout)
  // =============================================

  const acoPage = document.getElementById('aco-page');
  const canVariantId = acoPage ? acoPage.dataset.canVariantId : null;

  document.querySelectorAll('.aco-club-panel__cta').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      handleAddToCart(btn);
    });
  });

  async function handleAddToCart(btn) {
    const subVariantId = btn.dataset.variantId;
    const sellingPlanId = btn.dataset.sellingPlanId;

    if (!subVariantId) return;

    const ctaText = btn.querySelector('.aco-club-card__cta-text');
    const originalText = ctaText ? ctaText.textContent : btn.textContent;
    btn.disabled = true;
    btn.classList.add('aco-club-card__cta--loading');
    if (ctaText) ctaText.textContent = 'ADDING\u2026';

    // Build items array: subscription + ammo can
    const items = [];

    // 1. The subscription product (with selling plan)
    const subItem = { id: parseInt(subVariantId, 10), quantity: 1 };
    if (sellingPlanId) {
      subItem.selling_plan = parseInt(sellingPlanId, 10);
    }
    items.push(subItem);

    // 2. The ammo can (current product)
    if (canVariantId) {
      items.push({ id: parseInt(canVariantId, 10), quantity: 1 });
    }

    // 3. Shipping insurance (via SubscriptionInsuranceCore if available)
    try {
      if (window.SubscriptionInsuranceCore) {
        const isSubscription = !!sellingPlanId;
        const insuranceAction = await window.SubscriptionInsuranceCore.determineInsuranceAction(isSubscription, 30);

        // Remove the old line(s) by their current keys: a discount code in the cart re-keys lines (#PA52681, Oct 19).
        if (insuranceAction.action === 'swap') {
          await window.SubscriptionInsuranceCore.removeReplacedInsurance(null);
        }

        if (insuranceAction.action === 'add' || insuranceAction.action === 'swap') {
          const insuranceItem = window.SubscriptionInsuranceCore.buildInsuranceItem(
            insuranceAction.addType,
            insuranceAction.frequency
          );
          if (insuranceItem) {
            items.push(insuranceItem);
          }
        }
      }
    } catch (insuranceErr) {
      console.warn('[Ammo Can Offer] Insurance check failed, continuing without:', insuranceErr);
    }

    // Add all items to cart
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }

      // Redirect straight to checkout
      window.location.href = '/checkout';
    } catch (error) {
      console.error('[Ammo Can Offer] Add to cart failed:', error);

      const panel = btn.closest('.aco-club-panel');
      let errorEl = panel ? panel.querySelector('.aco-club-panel__error') : null;
      if (!errorEl && panel) {
        errorEl = document.createElement('p');
        errorEl.className = 'aco-club-panel__error';
        btn.insertAdjacentElement('afterend', errorEl);
      }
      if (errorEl) {
        errorEl.textContent = error.message || 'Something went wrong. Please try again.';
      }

      btn.disabled = false;
      btn.classList.remove('aco-club-card__cta--loading');
      if (ctaText) ctaText.textContent = originalText;
    }
  }

  // =============================================
  // 4. SCROLL REVEAL ANIMATIONS
  // =============================================

  const revealEls = document.querySelectorAll('.aco-reveal');

  if (prefersReducedMotion) {
    revealEls.forEach(el => el.classList.add('aco-reveal--visible'));
  } else if (revealEls.length) {
    const revealObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseInt(el.dataset.revealDelay, 10) || 0;
          if (delay > 0) {
            setTimeout(() => el.classList.add('aco-reveal--visible'), delay);
          } else {
            el.classList.add('aco-reveal--visible');
          }
          revealObserver.unobserve(el);
        });
      },
      { threshold: 0.15 }
    );
    revealEls.forEach(el => revealObserver.observe(el));
  }

  // =============================================
  // 5. STICKY MOBILE BAR
  // =============================================

  const stickyBar = document.querySelector('.aco-sticky-bar');
  const hero = document.querySelector('.aco-hero');

  if (stickyBar && hero) {
    const heroObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          stickyBar.classList.toggle('aco-sticky-bar--visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    heroObserver.observe(hero);
  }

  // =============================================
  // 6. SMOOTH SCROLL CTAs
  // =============================================

  document.querySelectorAll('[data-scroll-to]').forEach(btn => {
    btn.addEventListener('click', e => {
      const target = document.querySelector(btn.dataset.scrollTo);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // =============================================
  // 7. FAQ ACCORDION (single-open)
  // =============================================

  const faqDetails = document.querySelectorAll('.aco-faq details');
  faqDetails.forEach(detail => {
    detail.addEventListener('toggle', () => {
      if (!detail.open) return;
      faqDetails.forEach(other => {
        if (other !== detail && other.open) other.open = false;
      });
    });
  });

  // =============================================
  // 8. SCARCITY COUNTER ANIMATION
  // =============================================

  const counterEls = document.querySelectorAll('.aco-final-cta__number');

  if (counterEls.length) {
    const counterObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseInt(el.dataset.count, 10) || 0;
          animateCount(el, target, 1500);
          counterObserver.unobserve(el);
        });
      },
      { threshold: 0.25 }
    );
    counterEls.forEach(el => counterObserver.observe(el));
  }

  function animateCount(el, end, ms) {
    if (prefersReducedMotion) { el.textContent = end; return; }
    const start = performance.now();
    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / ms, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(eased * end);
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // =============================================
  // 9. HERO ENTRANCE
  // =============================================

  if (!prefersReducedMotion) {
    [
      { sel: '.aco-hero__kicker', delay: 200 },
      { sel: '.aco-hero__media', delay: 300 },
      { sel: '.aco-hero__headline', delay: 400 },
      { sel: '.aco-hero__subheadline', delay: 600 },
      { sel: '.aco-hero__cta', delay: 800 },
      { sel: '.aco-hero__trust', delay: 1000 }
    ].forEach(({ sel, delay }) => {
      const el = document.querySelector(sel);
      if (el) setTimeout(() => el.classList.add('aco-reveal--visible'), delay);
    });
  }

  // =============================================
  // 10. AUTO-SELECT "MOST POPULAR" TIER IN ACTIVE PANEL
  // =============================================

  const activePanel = document.querySelector('.aco-club-panel--active');
  if (activePanel) {
    const popularTier = activePanel.querySelector('.aco-tier-row--popular');
    if (popularTier) selectTier(popularTier);
  }
});